import { AlertTriangle, Info, Lightbulb, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { FunnelStep } from '@/data/dashboardMockData';

interface FunnelInsightsProps {
  funnelData: FunnelStep[];
  reguaNome?: string;
}

interface Insight {
  type: 'warning' | 'info' | 'success';
  icon: React.ReactNode;
  title: string;
  description: string;
}

function generateInsights(funnelData: FunnelStep[]): Insight[] {
  const insights: Insight[] = [];

  if (funnelData.length < 2) return insights;

  const enviados = funnelData[0]?.valor || 0;
  const entregues = funnelData[1]?.valor || 0;
  const lidos = funnelData[2]?.valor || 0;
  const clicaram = funnelData[3]?.valor || 0;
  const pagaram = funnelData[4]?.valor || 0;

  const taxaEntrega = enviados > 0 ? (entregues / enviados) * 100 : 0;
  const taxaLeitura = entregues > 0 ? (lidos / entregues) * 100 : 0;
  const taxaClique = lidos > 0 ? (clicaram / lidos) * 100 : 0;
  const taxaPagamento = clicaram > 0 ? (pagaram / clicaram) * 100 : 0;
  const taxaConversaoTotal = enviados > 0 ? (pagaram / enviados) * 100 : 0;

  // Alerta: Taxa de entrega baixa
  if (taxaEntrega < 90) {
    insights.push({
      type: 'warning',
      icon: <AlertTriangle className="h-4 w-4" />,
      title: `Taxa de Entrega Baixa (${taxaEntrega.toFixed(1)}%)`,
      description: 'Possível causa: cadastros incompletos (telefones ou e-mails inválidos). Revise a qualidade dos dados de contato.',
    });
  }

  // Info: Taxa de leitura em canais específicos
  if (taxaLeitura < 70) {
    insights.push({
      type: 'info',
      icon: <Info className="h-4 w-4" />,
      title: 'Nota sobre Taxa de Leitura',
      description: 'SMS e WhatsApp podem não refletir a realidade quando o cliente desativa a confirmação de leitura. E-mail tem métricas mais precisas.',
    });
  }

  // Alerta: Taxa de clique baixa
  if (taxaClique < 60) {
    insights.push({
      type: 'warning',
      icon: <AlertTriangle className="h-4 w-4" />,
      title: `Taxa de Clique Baixa (${taxaClique.toFixed(1)}%)`,
      description: 'O conteúdo da mensagem pode não estar claro ou o CTA não está atrativo. Considere revisar o texto e o botão de ação.',
    });
  }

  // Sucesso: Boa taxa de conversão
  if (taxaConversaoTotal >= 40) {
    insights.push({
      type: 'success',
      icon: <Lightbulb className="h-4 w-4" />,
      title: `Ótima Conversão (${taxaConversaoTotal.toFixed(1)}%)`,
      description: 'Esta régua está performando acima da média. Considere replicar as melhores práticas para outras réguas.',
    });
  }

  // Recomendação: Taxa de pagamento
  if (taxaPagamento >= 70) {
    insights.push({
      type: 'success',
      icon: <Sparkles className="h-4 w-4" />,
      title: 'Alta Conversão de Cliques em Pagamentos',
      description: 'Clientes que clicam têm alta propensão a pagar. Foque em aumentar a taxa de cliques para melhorar resultados.',
    });
  } else if (taxaPagamento < 50) {
    insights.push({
      type: 'warning',
      icon: <AlertTriangle className="h-4 w-4" />,
      title: `Baixa Conversão de Pagamento (${taxaPagamento.toFixed(1)}%)`,
      description: 'Muitos clientes clicam mas não pagam. Verifique se a página de pagamento está funcionando corretamente.',
    });
  }

  return insights.slice(0, 4); // Limita a 4 insights
}

const insightStyles = {
  warning: {
    bg: 'bg-[hsl(38,92%,95%)]',
    border: 'border-[hsl(38,92%,70%)]',
    icon: 'text-[hsl(38,92%,40%)]',
    title: 'text-[hsl(38,92%,30%)]',
  },
  info: {
    bg: 'bg-[hsl(220,70%,97%)]',
    border: 'border-[hsl(220,70%,80%)]',
    icon: 'text-[hsl(220,70%,50%)]',
    title: 'text-[hsl(220,70%,35%)]',
  },
  success: {
    bg: 'bg-[hsl(var(--accent-50))]',
    border: 'border-[hsl(var(--accent-200))]',
    icon: 'text-[hsl(var(--accent-600))]',
    title: 'text-[hsl(var(--accent-700))]',
  },
};

export function FunnelInsights({ funnelData, reguaNome }: FunnelInsightsProps) {
  const insights = generateInsights(funnelData);

  return (
    <div className="p-5 rounded-2xl border bg-card hover:shadow-lg transition-all duration-300 h-full">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="h-4 w-4 text-[hsl(270,70%,55%)]" />
        <h3 className="text-sm font-semibold text-foreground">Insights IA</h3>
      </div>

      {reguaNome && (
        <p className="text-xs text-muted-foreground mb-4">
          Análise da régua: <span className="font-medium">{reguaNome}</span>
        </p>
      )}

      {insights.length === 0 ? (
        <div className="p-4 text-center text-muted-foreground text-sm">
          Nenhum insight disponível para esta régua.
        </div>
      ) : (
        <div className="space-y-3">
          {insights.map((insight, index) => {
            const styles = insightStyles[insight.type];
            return (
              <div
                key={index}
                className={cn(
                  'p-3 rounded-lg border',
                  styles.bg,
                  styles.border
                )}
              >
                <div className="flex items-start gap-2">
                  <div className={cn('mt-0.5', styles.icon)}>
                    {insight.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={cn('text-xs font-semibold', styles.title)}>
                      {insight.title}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                      {insight.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
